import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { allCategories, catLabel } from "@/lib/lore";
import { useEntities, useInvalidate } from "@/lib/queries";

const clean = (t) => t.replace(/\s*\[(?:[EV]\d+)(?:\s*,\s*[EV]\d+)*\]/g, "").replace(/\*\*(.+?)\*\*/g, "$1").trim();
const firstSentences = (t) => (t.match(/[^.!?]+[.!?]+/g) || [t]).slice(0, 2).join(" ").trim();

const ModeToggle = ({ mode, setMode }) => (
  <div className="inline-flex rounded-md border border-[#2D3442] bg-[#0F131A] p-0.5">
    {[["new", "New entry"], ["append", "Add to existing"]].map(([k, l]) => (
      <button key={k} type="button" onClick={() => setMode(k)} data-testid={`save-answer-mode-${k}`}
        className={`rounded px-3 py-1.5 text-xs transition-colors ${mode === k ? "bg-[#1A202C] text-[#F3EBDD] shadow-[inset_0_-2px_0_#D97706]" : "text-[#A8A090] hover:text-[#F3EBDD]"}`}>{l}</button>
    ))}
  </div>
);

export const SaveAnswerDialog = ({ open, onOpenChange, uid, exchange, onSaved }) => {
  const invalidate = useInvalidate();
  const { data: entities = [] } = useEntities(uid);
  const citedIds = Object.values(exchange.citations || {}).filter((c) => c.type === "entry").map((c) => c.id);
  const body = clean(exchange.answer);
  const [mode, setMode] = useState("new");
  const [form, setForm] = useState({ name: "", category: "Lore", summary: firstSentences(body), notes: body, target: citedIds[0] || "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const sourceLine = `\n\n— From Ask the Archive: “${exchange.question}”`;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let id;
      if (mode === "new") {
        const { data } = await api.post(`/universes/${uid}/entities`, {
          name: form.name.trim(), category: form.category, summary: form.summary, content: form.notes + sourceLine, related_entity_ids: citedIds,
        });
        id = data.id;
      } else {
        const { data: cur } = await api.get(`/entities/${form.target}`);
        await api.patch(`/entities/${form.target}`, {
          content: [cur.content, form.notes + sourceLine].filter(Boolean).join("\n\n"),
          related_entity_ids: [...new Set([...(cur.related_entity_ids || []), ...citedIds.filter((x) => x !== form.target)])],
        });
        id = form.target;
      }
      invalidate(["entities", uid], ["entity", id]);
      toast.success(mode === "new" ? `${form.name.trim()} added to the Lore Bible` : "Answer added to the entry");
      onSaved(id);
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const valid = mode === "new" ? form.name.trim() : form.target;
  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto lf-scroll" data-testid="save-answer-dialog">
        <form onSubmit={save}>
          <DialogHeader>
            <DialogTitle className="font-display text-3xl">Save as lore</DialogTitle>
            <DialogDescription>Keep this answer in your Lore Bible. It'll be linked to the {citedIds.length} {citedIds.length === 1 ? "entry" : "entries"} it cites.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-5">
            <ModeToggle mode={mode} setMode={setMode} />
            {mode === "new" ? (
              <>
                <div className="grid grid-cols-[1fr_180px] gap-3">
                  <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={set("name")} placeholder="Kael's Coronation" data-testid="save-answer-name-input" /></div>
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select value={form.category} onValueChange={set("category")}>
                      <SelectTrigger data-testid="save-answer-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{allCategories().map((c) => <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2"><Label>Summary</Label><Textarea rows={2} value={form.summary} onChange={set("summary")} data-testid="save-answer-summary-input" /></div>
              </>
            ) : (
              <div className="space-y-2">
                <Label>Add to entry</Label>
                <Select value={form.target} onValueChange={set("target")}>
                  <SelectTrigger data-testid="save-answer-target-select"><SelectValue placeholder="Choose an entry" /></SelectTrigger>
                  <SelectContent>{entities.map((x) => <SelectItem key={x.id} value={x.id}>{x.name} · {catLabel(x.category)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2"><Label>Notes to save</Label><Textarea rows={6} value={form.notes} onChange={set("notes")} data-testid="save-answer-notes-input" /></div>
          </div>
          <DialogFooter><Button type="submit" disabled={busy || !valid} data-testid="save-answer-submit-button">{busy ? "Saving…" : mode === "new" ? "Create entry" : "Add to entry"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
